import { memo, type ReactNode } from 'react';

import { type CreditAnalyticsParams } from '@air/analytics';

import { type AiModelConfig } from './api/aimodels/useAiModels';
import { AgentProvider } from './providers/AgentProvider';
import { type GetAuthToken } from './types/subscriptions';
import { type Skill } from './ui/components/skills';

export type ChatProps = {
  agentUrl: string;
  children: ReactNode;
  firstName?: string;
  getAuthToken: GetAuthToken;
  canConsumeMoreCredits: boolean;
  resourceId: string;
  /** Called when resourceId is empty (draft mode). Must create the resource and return the real ID. */
  resolveResourceId?: () => Promise<string>;
  workspaceId: string;
  creditAnalyticsParams: CreditAnalyticsParams | null;
  onShowPlansModal?: () => void;
  /** Opens the host's edit-skill modal for a skill in the `/` picker. */
  onEditSkill?: (skill: Skill) => void;
  onCreateSkill?: () => void;
  /** Whether the current user can edit a given skill (drives the pencil affordance in the `/` picker). */
  canEditSkill?: (skill: Skill) => boolean;
  onGoToContext?: () => void;
  /**
   * Per-capability model overrides (capability name → model id) the host app merges into the
   * agent's `modelConfig.models`.
   */
  capabilityModelOverrides?: AiModelConfig['models'];
  /** Gates saved-size/set mention queries and the chat follow-up's set-save prompt. Default off. */
  savedSizesEnabled?: boolean;
  /** Whether the current member can create/update saved sizes and sets (read access is implicit). */
  canEditSavedSizes?: boolean;
  canvasAnimateVariantsEnabled?: boolean;
};

export const Chat = memo(
  ({
    agentUrl,
    canConsumeMoreCredits,
    children,
    firstName,
    getAuthToken,
    workspaceId,
    resourceId,
    creditAnalyticsParams,
    onShowPlansModal,
    onEditSkill,
    onCreateSkill,
    canEditSkill,
    onGoToContext,
    resolveResourceId,
    capabilityModelOverrides,
    savedSizesEnabled,
    canEditSavedSizes,
    canvasAnimateVariantsEnabled,
  }: ChatProps) => (
    <AgentProvider
      agentUrl={agentUrl}
      canConsumeMoreCredits={canConsumeMoreCredits}
      canEditSavedSizes={canEditSavedSizes}
      canEditSkill={canEditSkill}
      canvasAnimateVariantsEnabled={canvasAnimateVariantsEnabled}
      capabilityModelOverrides={capabilityModelOverrides}
      creditAnalyticsParams={creditAnalyticsParams}
      firstName={firstName}
      getAuthToken={getAuthToken}
      onCreateSkill={onCreateSkill}
      onEditSkill={onEditSkill}
      onGoToContext={onGoToContext}
      onShowPlansModal={onShowPlansModal}
      resolveResourceId={resolveResourceId}
      resourceId={resourceId}
      savedSizesEnabled={savedSizesEnabled}
      workspaceId={workspaceId}
    >
      {children}
    </AgentProvider>
  ),
);

Chat.displayName = 'Chat';
